import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Banner, Card, CardBody, CardHead, KV, KVRow, PageHead, StateBlock, Stat } from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { ApiError, post, useApi } from '../lib/api.ts'
import { useBetSlip, type SlipSelection } from '../lib/betslip.tsx'
import { gbp, odds } from '../lib/format.ts'
import { accumulatorPrice, BOOKIE_MARGIN } from '../../shared/odds.ts'
import type { BetslipPayload, BetsRequest, BetsResult, PlacedBet, SlipPrice, SlipProblem } from '../../shared/types.ts'

const keyOf = (s: { marketId: string; selectionId: number }) => `${s.marketId}:${s.selectionId}`

/** A stake as typed: a positive amount to the penny, or null for blank or nonsense. */
function stakeOf(text: string | undefined): number | null {
  const n = Number(text)
  return text && Number.isFinite(n) && n >= 0.01 ? Math.round(n * 100) / 100 : null
}

/**
 * The bet slip. Every selection on it can be backed as a single, each with its own stake,
 * and two or more from different games can also be combined into one accumulator. One
 * button places the lot. Prices are bookie prices and are refreshed while the slip is
 * open; a price that moves has to be accepted before the slip can go.
 */
export function BetSlip() {
  usePageTitle('Bet slip')
  const slip = useBetSlip()
  const sel = slip.selections.map(keyOf).join(',')
  const { data: fresh } = useApi<BetslipPayload>(sel ? `/betslip?sel=${encodeURIComponent(sel)}` : null, 15_000)

  const [stakes, setStakes] = useState<Record<string, string>>({})
  const [accStake, setAccStake] = useState('')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<BetsResult | null>(null)

  const now = new Map((fresh?.prices ?? []).map((p) => [keyOf(p), p]))
  const priceNow = (s: SlipSelection): SlipPrice | undefined => now.get(keyOf(s))
  const problemsFor = (bet: SlipProblem['bet'], s?: SlipSelection) =>
    (result?.problems ?? []).filter((p) => p.bet === bet && (!s || (p.marketId === s.marketId && p.selectionId === s.selectionId)))

  // A price differs from the one on the slip: it has to be accepted before anything goes.
  const changed = slip.selections.filter((s) => {
    const p = priceNow(s)
    return p?.price != null && p.price !== s.price
  })
  const unavailable = slip.selections.filter((s) => priceNow(s)?.unavailable)

  // One selection per game in an accumulator, as a bookmaker takes it.
  const games = slip.selections.map((s) => priceNow(s)?.eventId ?? s.event)
  const sameGame = new Set(games).size !== games.length
  const canAcc = slip.selections.length >= 2 && !sameGame
  const accOdds = canAcc ? accumulatorPrice(slip.selections.map((s) => s.price)) : null

  const singles = slip.selections
    .map((s) => ({ s, stake: stakeOf(stakes[keyOf(s)]) }))
    .filter((x): x is { s: SlipSelection; stake: number } => x.stake !== null)
  const acc = canAcc ? stakeOf(accStake) : null
  const totalStake = singles.reduce((t, x) => t + x.stake, 0) + (acc ?? 0)
  const totalReturns = singles.reduce((t, x) => t + x.stake * x.s.price, 0) + (acc && accOdds ? acc * accOdds : 0)
  const overBudget = fresh ? totalStake > fresh.available + 1e-9 : false

  const blocked = totalStake === 0 || changed.length > 0 || unavailable.length > 0 || overBudget || sending

  const place = async () => {
    const body: BetsRequest = {
      singles: singles.map(({ s, stake }) => ({ marketId: s.marketId, selectionId: s.selectionId, price: s.price, stake })),
      accumulator:
        acc !== null
          ? { legs: slip.selections.map((s) => ({ marketId: s.marketId, selectionId: s.selectionId, price: s.price })), stake: acc }
          : null,
    }
    setSending(true)
    try {
      const res = await post<BetsResult>('/bets', body)
      setResult(res)
      slip.clear()
      setStakes({})
      setAccStake('')
    } catch (err) {
      const refused = err instanceof ApiError ? (err.body as Partial<BetsResult>) : null
      setResult({
        ok: false,
        message: err instanceof Error ? err.message : String(err),
        problems: refused?.problems ?? [],
        prices: refused?.prices ?? [],
        placed: [],
      })
    } finally {
      setSending(false)
    }
  }

  const acceptPrices = () =>
    slip.reprice(changed.map((s) => ({ marketId: s.marketId, selectionId: s.selectionId, price: priceNow(s)!.price! })))

  // A refusal is a banner beside the slip; a placed slip gets its full confirmation.
  const resultBanner = result && !result.ok && (
    <Banner tone="halt" icon="⛔" title="Nothing was placed">
      {result.message}
    </Banner>
  )

  const reuse = (placed: PlacedBet[]) => {
    const seen = new Set<string>()
    for (const l of placed.flatMap((p) => p.legs)) {
      if (seen.has(keyOf(l)) || slip.has(l.marketId, l.selectionId)) continue
      seen.add(keyOf(l))
      slip.toggle({ marketId: l.marketId, selectionId: l.selectionId, event: l.event, marketType: l.marketType, runner: l.runner, price: l.price })
    }
    setResult(null)
  }

  if (result?.ok) {
    return (
      <main className="content">
        <PageHead title="Bet slip" />
        <BetPlaced placed={result.placed} onReuse={() => reuse(result.placed)} onClose={() => setResult(null)} />
      </main>
    )
  }

  if (slip.selections.length === 0) {
    return (
      <main className="content">
        <PageHead title="Bet slip" />
        {resultBanner}
        <Card>
          <CardBody>
            <StateBlock
              icon="◎"
              title="Your bet slip is empty"
              text="Tap a price on a game to add it. Add a few from different games to back them together as an accumulator."
              action={
                <Link className="btn primary" to="/" style={{ marginTop: 12 }}>
                  Browse games
                </Link>
              }
            />
          </CardBody>
        </Card>
      </main>
    )
  }

  return (
    <main className="content">
      <PageHead
        title="Bet slip"
        sub={`${slip.selections.length} selection${slip.selections.length === 1 ? '' : 's'} · bookie prices, no commission`}
        actions={
          <button className="btn outline" onClick={slip.clear}>
            Clear slip
          </button>
        }
      />

      <div className="grid cols-12">
        <div className="span-7 stack">
          {changed.length > 0 && (
            <Banner
              tone="degraded"
              title={`${changed.length} price${changed.length === 1 ? ' has' : 's have'} changed`}
              actions={
                <button className="btn primary sm" onClick={acceptPrices}>
                  Accept new prices
                </button>
              }
            >
              Accept the prices on offer now to place the slip.
            </Banner>
          )}

          <Card>
            <CardHead title="Singles" sub="A stake on each selection on its own. Leave a stake blank to skip it." />
            <CardBody flush>
              {slip.selections.map((s) => {
                const p = priceNow(s)
                const k = keyOf(s)
                const stake = stakeOf(stakes[k])
                const moved = p?.price != null && p.price !== s.price
                const problems = [...problemsFor('single', s), ...problemsFor('accumulator', s)]
                return (
                  <div className="slip-row" key={k}>
                    <div className="slip-main">
                      <div className="cell-strong">{s.runner}</div>
                      <div className="cell-sub">{[s.event, s.marketType].filter(Boolean).join(' · ')}</div>
                      {p?.unavailable && <div className="cell-sub neg">{p.unavailable}</div>}
                      {problems.map((x, i) => (
                        <div className="cell-sub neg" key={i}>
                          {x.text}
                        </div>
                      ))}
                    </div>
                    <div className="slip-price">
                      <span className="u-mono u-bold">{odds(s.price)}</span>
                      {moved && (
                        <span className={`cell-sub u-mono ${p!.price! > s.price ? 'pos' : 'neg'}`}>
                          now {odds(p!.price)}
                        </span>
                      )}
                    </div>
                    <div className="slip-stake">
                      <input
                        className="input mono"
                        inputMode="decimal"
                        placeholder="Stake"
                        aria-label={`Stake on ${s.runner}`}
                        value={stakes[k] ?? ''}
                        onChange={(e) => setStakes((prev) => ({ ...prev, [k]: e.target.value }))}
                      />
                      <span className="cell-sub">Returns {stake ? gbp(stake * s.price) : '—'}</span>
                    </div>
                    <button
                      className="btn ghost sm"
                      aria-label={`Remove ${s.runner} from the slip`}
                      onClick={() => slip.remove(s.marketId, s.selectionId)}
                    >
                      ✕
                    </button>
                  </div>
                )
              })}
            </CardBody>
          </Card>

          {slip.selections.length >= 2 && (
            <Card>
              <CardHead
                title={`${slip.selections.length}-fold accumulator`}
                sub="One stake on every selection. It wins only if they all do."
              />
              <CardBody>
                {sameGame ? (
                  <div className="u-faint">
                    Selections from the same game cannot go in one accumulator. Keep one selection per game to combine them.
                  </div>
                ) : (
                  <div className="slip-row" style={{ padding: 0, border: 0 }}>
                    <div className="slip-main">
                      <div className="cell-strong">Combined odds</div>
                      <div className="cell-sub">
                        {slip.selections.map((s) => odds(s.price)).join(' × ')}
                      </div>
                      {problemsFor('accumulator')
                        .filter((x) => x.marketId === undefined)
                        .map((x, i) => (
                          <div className="cell-sub neg" key={i}>
                            {x.text}
                          </div>
                        ))}
                    </div>
                    <div className="slip-price">
                      <span className="u-mono u-bold">{odds(accOdds)}</span>
                    </div>
                    <div className="slip-stake">
                      <input
                        className="input mono"
                        inputMode="decimal"
                        placeholder="Stake"
                        aria-label="Accumulator stake"
                        value={accStake}
                        onChange={(e) => setAccStake(e.target.value)}
                      />
                      <span className="cell-sub">Returns {acc && accOdds ? gbp(acc * accOdds) : '—'}</span>
                    </div>
                    <span style={{ width: 30 }} />
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </div>

        <div className="span-5 stack">
          {resultBanner}
          <Card pad>
            <KV>
              <KVRow label="Total stake">{gbp(totalStake)}</KVRow>
              <KVRow label="Potential returns">
                <span className="pos u-bold">{gbp(totalReturns)}</span>
              </KVRow>
            </KV>
            {overBudget && (
              <div className="cell-sub neg" style={{ marginTop: 10 }}>
                More than the {gbp(fresh!.available)} left in this round. Lower the stakes, or start a new round on{' '}
                <Link to="/bankroll">Bankroll</Link>.
              </div>
            )}
            <button className="btn primary block lg" style={{ marginTop: 14 }} disabled={blocked} onClick={() => void place()}>
              {sending ? 'Placing…' : totalStake > 0 ? `Place bets · ${gbp(totalStake)}` : 'Place bets'}
            </button>
            <div className="help" style={{ marginTop: 8, textAlign: 'center' }}>
              Everything on the slip is placed together, or nothing is.
            </div>
          </Card>
          {fresh && (
            <Card pad>
              <Stat label="Left to bet in this round" value={gbp(fresh.available)} />
            </Card>
          )}
          <div className="help">
            Prices are the best on the exchange less a {Math.round(BOOKIE_MARGIN * 100)}% margin. Returns include
            your stake; no commission is taken from winnings.
          </div>
        </div>
      </div>
    </main>
  )
}

/**
 * What a placed slip confirms, as a bookmaker's does: each bet with every selection and
 * its price, the game and market it is on, the bet's odds, stake and returns; then the
 * slip's total stake and total returns.
 */
function BetPlaced({ placed, onReuse, onClose }: { placed: PlacedBet[]; onReuse: () => void; onClose: () => void }) {
  const stake = placed.reduce((t, p) => t + p.stake, 0)
  const returns = placed.reduce((t, p) => t + p.returns, 0)
  const singles = placed.filter((p) => p.bet === 'single')
  const multiples = placed.filter((p) => p.bet === 'accumulator')

  return (
    <Card className="bet-placed">
      <CardHead
        title="Bet placed"
        right={
          <button className="btn ghost sm" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        }
      />
      <CardBody className="stack">
        <div className="bp-ok" role="status">
          <span aria-hidden="true">✓</span> {placed.length === 1 ? 'Bet placed successfully' : `${placed.length} bets placed successfully`}
        </div>

        {singles.length > 0 && <div className="lbl">Singles</div>}
        {singles.map((p) => (
          <div className="bp-bet" key={p.ref}>
            <PlacedLegs bet={p} />
            <PlacedFigures bet={p} />
          </div>
        ))}

        {multiples.length > 0 && <div className="lbl">Multiples</div>}
        {multiples.map((p) => (
          <div className="bp-bet" key={p.ref}>
            <div className="bp-head">{p.legs.length} selections</div>
            <PlacedLegs bet={p} />
            <div className="bp-fold">{p.legs.length} Fold</div>
            <PlacedFigures bet={p} />
          </div>
        ))}

        <div className="bp-totals">
          <div>
            <div className="cell-sub">Total stake</div>
            <div className="bp-total">{gbp(stake)}</div>
          </div>
          <div className="u-right">
            <div className="cell-sub">Total returns</div>
            <div className="bp-total">{gbp(returns)}</div>
          </div>
        </div>

        <button className="btn primary block lg" onClick={onReuse}>
          + Re-use selections
        </button>
        <div className="help" style={{ textAlign: 'center' }}>
          Your bets are on <Link to="/positions">Positions</Link> and <Link to="/pnl">P&amp;L</Link>.
        </div>
      </CardBody>
    </Card>
  )
}

function PlacedLegs({ bet }: { bet: PlacedBet }) {
  return (
    <div className="bp-legs">
      {bet.legs.map((l) => (
        <div className="bp-leg" key={keyOf(l)}>
          <div className="between">
            <span className="cell-strong">{l.runner}</span>
            <span className="u-mono u-bold">{odds(l.price)}</span>
          </div>
          <div className="cell-sub">{[l.marketType, l.event].filter(Boolean).join(' – ')}</div>
        </div>
      ))}
    </div>
  )
}

function PlacedFigures({ bet }: { bet: PlacedBet }) {
  return (
    <div className="bp-figures">
      <div>
        <div className="cell-sub">Odds</div>
        <div className="u-mono u-bold">{odds(bet.price)}</div>
      </div>
      <div>
        <div className="cell-sub">Stake</div>
        <div className="u-mono u-bold">{gbp(bet.stake)}</div>
      </div>
      <div className="u-right" style={{ marginLeft: 'auto' }}>
        <div className="cell-sub">Returns</div>
        <div className="u-mono u-bold">{gbp(bet.returns)}</div>
      </div>
    </div>
  )
}
